import * as Cesium from "cesium";

import { useEffect, useRef } from "react";

interface ViewerCompassProps {
  viewer: Cesium.Viewer | null;
  offsetLeftPx?: number;
}

const NORTH_RESET_DURATION_SECONDS = 0.8;

function normaliserCapEnDegres(capRadians: number): number {
  const capDegres = Cesium.Math.toDegrees(capRadians);

  return ((capDegres % 360) + 360) % 360;
}

/**
 * Boussole superposée au viewer pour afficher l'orientation caméra
 * et permettre un recentrage rapide vers le nord.
 */
export function ViewerCompass({
  viewer,
  offsetLeftPx = 16,
}: ViewerCompassProps) {
  const aiguilleRef = useRef<HTMLSpanElement | null>(null);
  const valeurRef = useRef<HTMLSpanElement | null>(null);
  const frameRef = useRef<number | null>(null);
  const capArrondiRef = useRef(0);

  useEffect(() => {
    const annulerFrame = () => {
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };

    const appliquerCap = (capDegres: number) => {
      if (aiguilleRef.current) {
        aiguilleRef.current.style.transform = `rotate(${-capDegres}deg)`;
      }

      const capArrondi = Math.round(capDegres);
      if (valeurRef.current && capArrondi !== capArrondiRef.current) {
        valeurRef.current.textContent = `${capArrondi}°`;
        capArrondiRef.current = capArrondi;
      }
    };

    if (!viewer || viewer.isDestroyed()) {
      annulerFrame();
      capArrondiRef.current = 0;
      appliquerCap(0);
      return () => {
        annulerFrame();
      };
    }

    const synchroniserCap = () => {
      frameRef.current = null;

      if (viewer.isDestroyed()) {
        return;
      }

      appliquerCap(normaliserCapEnDegres(viewer.camera.heading));
    };

    const planifierSynchronisation = () => {
      if (frameRef.current !== null) {
        return;
      }

      frameRef.current = window.requestAnimationFrame(synchroniserCap);
    };

    capArrondiRef.current = Math.round(normaliserCapEnDegres(viewer.camera.heading));
    appliquerCap(normaliserCapEnDegres(viewer.camera.heading));
    viewer.camera.changed.addEventListener(planifierSynchronisation);

    return () => {
      annulerFrame();
      if (!viewer.isDestroyed()) {
        viewer.camera.changed.removeEventListener(planifierSynchronisation);
      }
    };
  }, [viewer]);

  const resetterVersLeNord = () => {
    if (!viewer || viewer.isDestroyed()) {
      return;
    }

    viewer.camera.flyTo({
      destination: viewer.camera.positionWC,
      duration: NORTH_RESET_DURATION_SECONDS,
      orientation: {
        heading: 0,
        pitch: viewer.camera.pitch,
        roll: viewer.camera.roll,
      },
    });
  };

  return (
    <button
        type="button"
        disabled={!viewer}
        onClick={resetterVersLeNord}
        className="absolute z-[9997] flex h-20 w-20 cursor-pointer items-center justify-center rounded-2xl border border-white/25 bg-slate-950/28 text-white/92 shadow-[0_8px_18px_rgba(15,23,42,0.22)] backdrop-blur-md transition-transform hover:scale-[1.03] hover:bg-slate-950/34 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
        style={{
          top: "calc(var(--viewer-topbar-height, 80px) + 16px)",
          left: `${offsetLeftPx}px`,
          transition:
            "left 300ms ease, top 300ms ease, transform 200ms ease, background-color 200ms ease",
        }}
        aria-label="Réorienter la caméra vers le nord"
        title="Réorienter vers le nord"
      >
        <span className="pointer-events-none absolute top-1.5 text-[10px] font-semibold tracking-[0.28em] text-white/90">
          N
        </span>
        <span className="pointer-events-none absolute left-2.5 text-[10px] font-medium tracking-[0.2em] text-white/65">
          O
        </span>
        <span className="pointer-events-none absolute right-2 text-[10px] font-medium tracking-[0.2em] text-white/65">
          E
        </span>

        <span
          ref={aiguilleRef}
          className="relative flex h-11 w-11 items-center justify-center transition-transform duration-150 ease-out"
          style={{ transform: "rotate(0deg)" }}
        >
          <svg
            viewBox="0 0 48 48"
            className="h-11 w-11 drop-shadow-[0_4px_10px_rgba(15,23,42,0.3)]"
            aria-hidden="true"
          >
            <path
              d="M24 4L31 24L24 20L17 24L24 4Z"
              fill="#f8fafc"
            />
            <path
              d="M24 44L17 24L24 28L31 24L24 44Z"
              fill="#cbd5e1"
              fillOpacity="0.5"
            />
            <path
              d="M24 20L31 24L24 28L17 24L24 20Z"
              fill="#0f172a"
              fillOpacity="0.85"
            />
          </svg>
          <span className="absolute h-2.5 w-2.5 rounded-full border border-white/40 bg-white shadow-sm" />
        </span>

        <span
          ref={valeurRef}
          className="pointer-events-none absolute bottom-1.5 rounded-full border border-white/15 bg-white/10 px-1.5 py-0.5 text-[10px] font-medium leading-none text-white/85"
        >
          0°
        </span>
      </button>
  );
}
